import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  contentTypesClearIconsMapping,
  contentTypesMapping,
} from '../../../utils/contentTypes';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../components/Filters/components/CheckboxList';
import { ReactComponent as WebIcon } from '../../../assets/web.svg';
import { useSearchContext } from '../contexts';

export const ContentTypeChooser = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters } = useSearchContext();

  const selected = state.draftFilters.contentTypes.value;

  const contentTypeList: {
    name: string;
    value: string;
    icon: React.ReactNode;
  }[] = Object.keys(contentTypesMapping).map((type) => ({
    name: contentTypesMapping[type as keyof typeof contentTypesMapping] || type,
    value: type,
    icon: contentTypesClearIconsMapping[
      type as keyof typeof contentTypesClearIconsMapping
    ] || <WebIcon />,
  }));

  const handleSelectAll = () => {
    updateDraftFilters({
      contentTypes: {
        value: contentTypeList.map((ct) => ct.value),
        exclude: state.draftFilters.contentTypes.exclude,
      },
    });
  };

  const handleClearAll = () => {
    updateDraftFilters({
      contentTypes: {
        value: [],
        exclude: false,
      },
    });
  };

  const handleToggleContentType = (contentTypeValue: string) => {
    if (selected.includes(contentTypeValue)) {
      updateDraftFilters({
        contentTypes: {
          value: selected.filter((ct) => ct !== contentTypeValue),
          exclude: state.draftFilters.contentTypes.exclude,
        },
      });
    } else {
      updateDraftFilters({
        contentTypes: {
          value: [...selected, contentTypeValue],
          exclude: state.draftFilters.contentTypes.exclude,
        },
      });
    }
  };

  // Memoize the checkbox items to prevent recreating on every render
  const checkboxItems = useMemo(() => {
    return contentTypeList.map((contentType) => ({
      name: t(contentType.name),
      checked: selected.includes(String(contentType.value)),
      onChange: () => handleToggleContentType(String(contentType.value)),
      disabled: false,
      icon: contentType.icon,
    }));
  }, [contentTypeList, selected, t]);

  return (
    <ContentContainer
      searchComponent={null}
      actionsComponent={
        <ControlsPanel
          onSelectAll={handleSelectAll}
          onClearAll={handleClearAll}
          count={selected.length}
          showExcludeSwitch={false}
          isExclude={state.draftFilters.contentTypes.exclude}
          showSelectAll={true}
          isSelectAllDisabled={selected.length === contentTypeList.length}
          id="content-type-filter"
        />
      }
      badgesComponent={undefined}
    >
      <CheckboxList items={checkboxItems} />
    </ContentContainer>
  );
};
